import { createContext, useReducer, useEffect } from 'react';
import { useParams } from 'react-router';
import { useGetSubmissionInsightsDataQuery } from '@api/benchmarking';
import { SubmissionInsightsData } from '@g17eco/types/portfolio';
import { InitiativeCompany } from '@g17eco/types/initiativeSurvey';
import { saveSubmissionInsightsFilters } from '@utils/portfolioFilterStorage';
import { useBenchmarkingDateRange } from '../BenchmarkingDateRangeContext';
import {
  SubmissionInsightsFilters,
  SubmissionInsightsFiltersWithDateRange,
  SubmissionInsightsView,
  MetricView,
} from './types';

interface SubmissionInsightsContainerProps {
  children: any;
}

interface SubmissionInsightsContextProps {
  isLoading: boolean;
  tableData: SubmissionInsightsData;
  companies: Pick<InitiativeCompany, '_id' | 'name' | 'type' | 'requestedDataShares'>[],
  handleApplyFilters: () => void;
  filters: SubmissionInsightsFilters;
  scopes: string[];
  error?: string;
  dispatch: React.Dispatch<any>;
}

// Initial state - dateRange/timeRange come from context, not local state
const initialState: SubmissionInsightsContextProps = {
  isLoading: false,
  tableData: { utrs: [], utrValues: [] },
  companies: [],
  handleApplyFilters: () => { },
  filters: {
    view: SubmissionInsightsView.Company,
    metricView: MetricView.Total,
  },
  scopes: [],
  dispatch: () => { },
};

export const SubmissionInsightsContext = createContext<SubmissionInsightsContextProps>(initialState);

export const ACTION = {
  SET_FILTERS: 'filters',
};

const reducer = (state: any, action: any) => {
  if (!action.type) {
    return state;
  }
  return { ...state, [action.type]: { ...state[action.type], ...action.payload } };
};

export const SubmissionInsightsContainer = (props: SubmissionInsightsContainerProps) => {
  const { portfolioId } = useParams<{ portfolioId: string }>();
  const { dateRange, timeRange } = useBenchmarkingDateRange();
  const [state, dispatch] = useReducer(reducer, initialState);

  // Save filters to sessionStorage whenever they change (dateRange/timeRange handled by context)
  useEffect(() => {
    saveSubmissionInsightsFilters(portfolioId, state.filters);
  }, [portfolioId, state.filters]);

  // Combine local filters with context date/time for API query
  const filtersWithDateRange: SubmissionInsightsFiltersWithDateRange = {
    ...state.filters,
    dateRange,
    timeRange,
  };

  const queryParams = {
    portfolioId,
    filters: filtersWithDateRange,
  };

  const query = useGetSubmissionInsightsDataQuery(queryParams, { skip: !portfolioId });
  const { scopeGroup, companies, utrValues, utrs } = query.data ?? {};

  const contextValue: SubmissionInsightsContextProps = {
    ...state,
    companies,
    tableData: {
      utrs,
      utrValues,
    },
    filters: {
      scopeGroup,
      ...state.filters,
    },
    isLoading: query.isFetching || query.isLoading,
    error: query.error?.message,
    dispatch,
  };

  return <SubmissionInsightsContext.Provider value={contextValue}>{props.children}</SubmissionInsightsContext.Provider>;
};
